import type React from "react";
import { Checkbox } from "components/designV2/checkbox/checkbox";
import {
  EmptyState,
  List,
  ListScroll,
  Option,
  OptionCheckbox,
  OptionLabel,
  OptionRight,
  OptionRightText,
} from "./searchableDropdown.styles";
import type { SearchableDropdownOption } from "./searchableDropdown.types";

interface SearchableDropdownListBaseProps {
  readonly emptyText: string;
  readonly filteredOptions: readonly SearchableDropdownOption[];
  readonly highlightedIndex: number;
  readonly isOpen: boolean;
  readonly labelId?: string;
  readonly listboxId: string;
  readonly listRef: React.Ref<HTMLUListElement>;
  readonly multiSelect?: boolean;
  readonly selectedValueSet: ReadonlySet<string>;
  readonly shouldHighlightOnHover: boolean;
  readonly value?: string;
  readonly zIndex: number;
  readonly onHighlight: (index: number) => void;
  readonly onSelect: (option: SearchableDropdownOption) => void;
}

type SearchableDropdownListPlacementProps =
  | { readonly placement: "inline" }
  | {
      readonly left: number;
      readonly placement: "portal";
      readonly top: number;
      readonly width: number;
    };

type StyledListPlacementProps =
  | { readonly $placement: "inline" }
  | {
      readonly $left: number;
      readonly $placement: "portal";
      readonly $top: number;
      readonly $width: number;
    };

export type SearchableDropdownListProps = SearchableDropdownListBaseProps &
  SearchableDropdownListPlacementProps;

export const SearchableDropdownList = (
  props: SearchableDropdownListProps,
): React.ReactElement => {
  const placementProps: StyledListPlacementProps =
    props.placement === "portal"
      ? {
          $placement: "portal",
          $left: props.left,
          $top: props.top,
          $width: props.width,
        }
      : { $placement: props.placement };

  return (
    <List
      id={props.listboxId}
      ref={props.listRef}
      role="listbox"
      aria-labelledby={props.labelId}
      aria-multiselectable={props.multiSelect ? "true" : undefined}
      $isOpen={props.isOpen}
      $zIndex={props.zIndex}
      {...placementProps}
    >
      <ListScroll>
        {props.filteredOptions.map((option, index) => {
          const isSelected = props.multiSelect
            ? props.selectedValueSet.has(option.value)
            : option.value === props.value;
          const isHighlighted = index === props.highlightedIndex;
          return (
            <Option
              key={option.value}
              id={`${props.listboxId}-option-${index}`}
              role="option"
              aria-selected={isSelected}
              $isSelected={isSelected}
              $isHighlighted={isHighlighted}
              $isMultiSelect={Boolean(props.multiSelect)}
              onMouseEnter={
                props.shouldHighlightOnHover
                  ? () => props.onHighlight(index)
                  : undefined
              }
              onClick={() => props.onSelect(option)}
            >
              <OptionLabel>{option.label}</OptionLabel>
              <OptionRight>
                {option.rightText && (
                  <OptionRightText>{option.rightText}</OptionRightText>
                )}
                {props.multiSelect && (
                  <OptionCheckbox onClick={(event) => event.stopPropagation()}>
                    <Checkbox
                      checked={isSelected ? "checked" : "unchecked"}
                      onChange={() => props.onSelect(option)}
                    />
                  </OptionCheckbox>
                )}
              </OptionRight>
            </Option>
          );
        })}
        {props.filteredOptions.length === 0 && (
          <EmptyState>{props.emptyText}</EmptyState>
        )}
      </ListScroll>
    </List>
  );
};
